import { PageRoots, portalPaths, UrlParams } from '@provision/portal-routes';
import { useEffect } from 'react';
import { Route } from 'react-router-dom';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { UserGroupRules } from 'src/graphql';
import {
  getNotificationEntityName,
  getNotificationTargetPath,
} from 'src/graphql/entities/notification/notification-target';
import { parseNotificationCompositeKey } from 'src/graphql/entities/notification/utils';
import { EntityNames } from 'src/graphql/types';
import { useSelectedConversation } from 'src/pages/Conversations';
import { ProtectedPage } from 'src/routes/components/protected-page';
import { getRoutePath } from 'src/routes/utils';
import useNavigationStore from 'src/store/navigation';
import { optionalRouteParam, useNavigateRoute, useRouteParams } from 'src/utils/routing';

const DEFAULT_LANDING_PATH = portalPaths.entity.all({ entityName: 'conversations' });

// old inbox page's deep links keyed selection by buildNotificationCompositeKey(entity, entityId);
// this redirects them onto the standard entity routes now that the list lives in the left pane.
const InboxRedirect = () => {
  const { navigate } = useNavigateRoute();
  const { recordId } = useRouteParams(UrlParams.recordId);
  const parsed = recordId ? parseNotificationCompositeKey(recordId) : null;
  const entityName = getNotificationEntityName(parsed?.entity);

  const { selectedConversation, isLoading } = useSelectedConversation({
    conversationId: entityName === EntityNames.Conversation ? (parsed?.entityId ?? '') : '',
  });

  useEffect(() => {
    useNavigationStore.getState().setPendingPane('inbox');
  }, []);

  useEffect(() => {
    if (!recordId) {
      navigate(DEFAULT_LANDING_PATH, { replace: true });
      return;
    }

    if (!parsed) {
      navigate(DEFAULT_LANDING_PATH, { replace: true });
      return;
    }

    // wait for the record to resolve so we have a friendlyId to route to; once the
    // query settles without one there is nothing left to wait for and the missing
    // friendlyId falls through to the landing view below
    if (entityName === EntityNames.Conversation && isLoading) {
      return;
    }

    const targetPath = getNotificationTargetPath({
      entityName,
      entityId: parsed.entityId,
      conversationFriendlyId: selectedConversation?.friendlyId,
    });

    // platformUser (and any other unmapped entity) has no standard view to open
    navigate(targetPath ?? DEFAULT_LANDING_PATH, { replace: true });
  }, [recordId, parsed, entityName, selectedConversation, isLoading, navigate]);

  return null;
};

export const InboxRoutes = ({ relative }: { relative?: boolean }) => (
  <Route
    element={
      <ProtectedPage
        redirectTo={PageRoots.conversation}
        allowedRules={[UserGroupRules.AccessToInbox]}
      />
    }
  >
    <Route
      path={`${getRoutePath(PageRoots.inbox, relative)}/${optionalRouteParam(UrlParams.recordId)}`}
      element={
        <MetaTags title="Inbox">
          <InboxRedirect />
        </MetaTags>
      }
    />
  </Route>
);
